import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import FormControl from 'react-bootstrap/FormControl';
import cx from 'classnames';

import Link from '../../Link';
import Loader from '../../Common/Loader';
import updateCategoryStatus from '../../../actions/siteadmin/Category/updateCategoryStatus';
import deleteCategory from '../../../actions/siteadmin/Category/deleteCategory';
import duplicateCategory from '../../../actions/siteadmin/Category/duplicateCategory';
import reorderCategories from '../../../actions/siteadmin/Category/reorderCategories';
import reorderSubCategories from '../../../actions/siteadmin/Category/reorderSubCategories';
import { saveSubCategoryInline, setSubCategoryStatusInline, deleteSubCategoryInline } from '../../../actions/siteadmin/SubCategory/subCategoryInline';
import { demoRestriction } from '../../../actions/demoRestriction';
import debounce from '../../../helpers/debounce';
import messages from '../../../locale/messages';
import { api, categoryUploadDir } from '../../../config';

import { CommonAdminEditIcon, CommonAdminTrashIcon } from '../../Common/Assets/SVGIcon/CommonSvgIcon';

import bt from '../../../components/commonStyle.css';
import s from './CategoryTree.css';

// Reorder a copy of `arr` by moving the item at `from` to `to`.
function move(arr, from, to) {
  const next = arr.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function CategoryTree({ categoryDetails, intl }) {
  const dispatch = useDispatch();
  const { formatMessage } = intl;
  const { getCategoryTree, loading, refetch } = categoryDetails;

  const [list, setList] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pricingFilter, setPricingFilter] = useState('all');
  const [popularOnly, setPopularOnly] = useState(false);
  const [dragId, setDragId] = useState(null);          // category being dragged
  const [subDrag, setSubDrag] = useState({ parent: null, id: null }); // subcategory drag
  const [newSubName, setNewSubName] = useState({});    // { [catId]: draft name }
  const [editingSub, setEditingSub] = useState(null);  // { id, name }
  const didInitExpand = useRef(false);

  // Keep local list in sync with server data; expand everything on first load so
  // subcategories are visible without hunting for the chevron.
  useEffect(() => {
    const results = getCategoryTree?.results ? getCategoryTree.results.slice() : [];
    setList(results);
    if (!didInitExpand.current && results.length > 0) {
      const all = {};
      results.forEach((c) => { if ((c.subCategories || []).length > 0) all[c.id] = true; });
      setExpanded(all);
      didInitExpand.current = true;
    }
  }, [getCategoryTree]);

  const expandAll = () => {
    const all = {};
    list.forEach((c) => { if ((c.subCategories || []).length > 0) all[c.id] = true; });
    setExpanded(all);
  };
  const collapseAll = () => setExpanded({});
  const anyExpanded = Object.values(expanded).some(Boolean);

  const handleSearch = useCallback(debounce((value) => setSearch(value)), []);

  const filtersActive = search.trim() !== '' || statusFilter !== 'all' || pricingFilter !== 'all' || popularOnly;

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return list.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (pricingFilter !== 'all' && c.pricingType !== pricingFilter) return false;
      if (popularOnly && !c.isPopular) return false;
      if (term && !(c.name || '').toLowerCase().includes(term)) return false;
      return true;
    });
  }, [list, search, statusFilter, pricingFilter, popularOnly]);

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  // ---- Category drag & drop (only when no filter narrows the view) ----
  const handleCatDrop = async (targetId) => {
    if (filtersActive || dragId == null || dragId === targetId) { setDragId(null); return; }
    const from = list.findIndex((c) => c.id === dragId);
    const to = list.findIndex((c) => c.id === targetId);
    if (from < 0 || to < 0) { setDragId(null); return; }
    const next = move(list, from, to);
    setList(next);
    setDragId(null);
    const res = await dispatch(reorderCategories(next.map((c) => c.id)));
    if (res?.status !== 200) refetch();
  };

  // ---- Subcategory drag & drop within a parent ----
  const handleSubDrop = async (parent, targetSubId) => {
    const { parent: dragParent, id } = subDrag;
    setSubDrag({ parent: null, id: null });
    if (filtersActive || id == null || dragParent !== parent.id || id === targetSubId) return;
    const subs = (parent.subCategories || []).slice();
    const from = subs.findIndex((x) => x.id === id);
    const to = subs.findIndex((x) => x.id === targetSubId);
    if (from < 0 || to < 0) return;
    const nextSubs = move(subs, from, to);
    // optimistic local update
    setList((cur) => cur.map((c) => (c.id === parent.id ? { ...c, subCategories: nextSubs } : c)));
    const res = await dispatch(reorderSubCategories(parent.id, nextSubs.map((x) => x.id)));
    if (res?.status !== 200) refetch();
  };

  const handleStatusChange = async (id, fieldName, value) => {
    const isRestrict = await dispatch(demoRestriction({ restrictType: 'category', restrictValue: id }));
    if (isRestrict) return;
    // optimistic
    setList((cur) => cur.map((c) => (c.id === id
      ? { ...c, [fieldName]: fieldName === 'isPopular' ? value === 'true' : value }
      : c)));
    await dispatch(updateCategoryStatus(id, 1, fieldName, value));
    refetch();
  };

  const handleDuplicate = async (id) => {
    const isRestrict = await dispatch(demoRestriction({ restrictType: 'category', restrictValue: id }));
    if (isRestrict) return;
    const res = await dispatch(duplicateCategory(id));
    if (res?.status === 200) refetch();
  };

  const handleDelete = async (id) => {
    const isRestrict = await dispatch(demoRestriction({ restrictType: 'category', restrictValue: id }));
    if (isRestrict) return;
    const res = await dispatch(deleteCategory(id, 1));
    if (res?.status === 200) refetch();
  };

  // ---- Inline subcategory management ----
  const handleAddSub = async (catId) => {
    const name = (newSubName[catId] || '').trim();
    if (!name) return;
    if (await dispatch(demoRestriction({ restrictType: 'category', restrictValue: catId }))) return;
    const res = await dispatch(saveSubCategoryInline(null, name, catId, 'active'));
    if (res?.status === 200) {
      setNewSubName((m) => ({ ...m, [catId]: '' }));
      refetch();
    }
  };

  const handleRenameSub = async (sub) => {
    const name = (editingSub?.name || '').trim();
    if (!name) { setEditingSub(null); return; }
    if (await dispatch(demoRestriction({ restrictType: 'category', restrictValue: sub.categoryId }))) return;
    const res = await dispatch(saveSubCategoryInline(sub.id, name, sub.categoryId, sub.status));
    setEditingSub(null);
    if (res?.status === 200) refetch();
  };

  const handleSubStatus = async (sub, value) => {
    if (await dispatch(demoRestriction({ restrictType: 'category', restrictValue: sub.categoryId }))) return;
    const res = await dispatch(setSubCategoryStatusInline(sub.id, value));
    if (res?.status === 200) refetch();
  };

  const handleDeleteSub = async (sub) => {
    if (await dispatch(demoRestriction({ restrictType: 'category', restrictValue: sub.categoryId }))) return;
    const res = await dispatch(deleteSubCategoryInline(sub.id));
    if (res?.status === 200) refetch();
  };

  return (
    <div className="whiteDropdown">
      {/* Toolbar: search + filters + add */}
      <div className={s.toolbar}>
        <div className={s.filters}>
          <div className={cx(s.searchInput, 'searchInputRTL')}>
            <FormControl
              type="text"
              placeholder={formatMessage(messages.searchOnly)}
              onChange={(e) => handleSearch(e.target.value)}
              className={bt.formControlInput}
            />
          </div>
          <select className={cx(bt.selectInput, s.filterSelect)} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="active">{formatMessage(messages.active)}</option>
            <option value="inactive">{formatMessage(messages.inactive)}</option>
          </select>
          <select className={cx(bt.selectInput, s.filterSelect)} value={pricingFilter} onChange={(e) => setPricingFilter(e.target.value)}>
            <option value="all">All pricing</option>
            <option value="fixed">{formatMessage(messages.fixedFareType)}</option>
            <option value="hourly">{formatMessage(messages.hourlyFareType)}</option>
          </select>
          <label className={s.popularToggle}>
            <input type="checkbox" checked={popularOnly} onChange={(e) => setPopularOnly(e.target.checked)} />
            <span>Popular only</span>
          </label>
        </div>
        <div className={s.toolbarActions}>
          <button type="button" className={s.expandBtn} onClick={anyExpanded ? collapseAll : expandAll}>
            {anyExpanded ? '▾ Collapse all' : '▸ Expand all'}
          </button>
          <Link to="/siteadmin/category/add" className={cx(bt.btnPrimary, bt.displayInlineBlock)}>
            {formatMessage(messages.addCategory)}
          </Link>
        </div>
      </div>

      {filtersActive && (
        <div className={s.reorderHint}>Clear filters to drag &amp; drop reorder.</div>
      )}

      {loading ? (
        <Loader type="circle" />
      ) : visible.length === 0 ? (
        <div className={s.empty}><FormattedMessage {...messages.noResult} /></div>
      ) : (
        <ul className={s.tree}>
          {visible.map((cat) => {
            const subs = cat.subCategories || [];
            const isOpen = !!expanded[cat.id];
            const thumbFile = cat.icon || cat.logoImage;
            const thumb = thumbFile ? api?.apiEndpoint + categoryUploadDir + 'medium_' + thumbFile : null;
            return (
              <li key={cat.id} className={s.catItem}>
                <div
                  className={cx(s.catRow, dragId === cat.id && s.dragging)}
                  draggable={!filtersActive}
                  onDragStart={() => setDragId(cat.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleCatDrop(cat.id)}
                >
                  <span className={s.handle} title="Drag to reorder">⠿</span>

                  <button
                    type="button"
                    className={s.caret}
                    onClick={() => toggleExpand(cat.id)}
                    aria-label="expand"
                  >
                    {isOpen ? '▾' : '▸'}
                  </button>

                  {thumb ? (
                    <img className={s.thumb} src={thumb} alt="" />
                  ) : (
                    <span className={s.thumbPlaceholder}>{(cat.name || '?').charAt(0)}</span>
                  )}

                  <div
                    className={s.nameCol}
                    onClick={() => toggleExpand(cat.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className={s.name}>{cat.name}</span>
                    <span className={s.meta}>
                      #{cat.id} · {cat.slug || '—'} ·{' '}
                      {subs.length > 0
                        ? <span className={s.subCount}>{subs.length} sub-services {isOpen ? '▾' : '▸'}</span>
                        : 'no sub-services'}
                    </span>
                  </div>

                  {cat.isPopular && <span className={s.badgePopular}>Popular</span>}
                  <span className={cx(s.badgeType)}>{cat.pricingType}</span>

                  <select
                    className={cx(bt.selectInput, s.statusSelect, cat.status === 'active' ? s.statusActive : s.statusInactive)}
                    value={cat.status}
                    onChange={(e) => handleStatusChange(cat.id, 'status', e.target.value)}
                  >
                    <option value="active">{formatMessage(messages.active)}</option>
                    <option value="inactive">{formatMessage(messages.inactive)}</option>
                  </select>

                  <select
                    className={cx(bt.selectInput, s.popularSelect)}
                    value={cat.isPopular ? 'true' : 'false'}
                    onChange={(e) => handleStatusChange(cat.id, 'isPopular', e.target.value)}
                  >
                    <option value="true">{formatMessage(messages.yes)}</option>
                    <option value="false">{formatMessage(messages.no)}</option>
                  </select>

                  <div className={s.actions}>
                    <Link to={`/siteadmin/category/edit/${cat.id}`} className={s.actionLink} title={formatMessage(messages.editAction)}>
                      <CommonAdminEditIcon className={s.icon} />
                    </Link>
                    <button type="button" className={s.actionBtn} onClick={() => handleDuplicate(cat.id)} title="Duplicate">⧉</button>
                    <button type="button" className={s.actionBtn} onClick={() => handleDelete(cat.id)} title={formatMessage(messages.deleteAction)}>
                      <CommonAdminTrashIcon className={s.icon} />
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <ul className={s.subTree}>
                    {subs.map((sub) => (
                      <li
                        key={sub.id}
                        className={cx(s.subRow, subDrag.id === sub.id && s.dragging)}
                        draggable={!filtersActive && editingSub?.id !== sub.id}
                        onDragStart={() => setSubDrag({ parent: cat.id, id: sub.id })}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => handleSubDrop(cat, sub.id)}
                      >
                        <span className={s.handle} title="Drag to reorder">⠿</span>

                        {editingSub?.id === sub.id ? (
                          <input
                            className={cx(bt.formControlInput, s.subInput)}
                            value={editingSub.name}
                            autoFocus
                            onChange={(e) => setEditingSub({ id: sub.id, name: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleRenameSub(sub); if (e.key === 'Escape') setEditingSub(null); }}
                            onBlur={() => handleRenameSub(sub)}
                          />
                        ) : (
                          <span className={s.subName} onClick={() => setEditingSub({ id: sub.id, name: sub.name })} style={{ cursor: 'text' }}>
                            {sub.name}
                          </span>
                        )}

                        <select
                          className={cx(bt.selectInput, s.subStatusSelect, sub.status === 'active' ? s.statusActive : s.statusInactive)}
                          value={sub.status}
                          onChange={(e) => handleSubStatus(sub, e.target.value)}
                        >
                          <option value="active">{formatMessage(messages.active)}</option>
                          <option value="inactive">{formatMessage(messages.inactive)}</option>
                        </select>

                        <div className={s.actions}>
                          <button type="button" className={s.actionBtn} onClick={() => setEditingSub({ id: sub.id, name: sub.name })} title={formatMessage(messages.editAction)}>
                            <CommonAdminEditIcon className={s.icon} />
                          </button>
                          <button type="button" className={s.actionBtn} onClick={() => handleDeleteSub(sub)} title={formatMessage(messages.deleteAction)}>
                            <CommonAdminTrashIcon className={s.icon} />
                          </button>
                        </div>
                      </li>
                    ))}

                    {/* Add sub-service inline */}
                    <li className={s.subAddRow}>
                      <span className={s.handle} style={{ visibility: 'hidden' }}>⠿</span>
                      <input
                        className={cx(bt.formControlInput, s.subInput)}
                        placeholder="+ Add a sub-service…"
                        value={newSubName[cat.id] || ''}
                        onChange={(e) => setNewSubName((m) => ({ ...m, [cat.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddSub(cat.id); }}
                      />
                      <button type="button" className={cx(bt.btnPrimary, s.subAddBtn)} onClick={() => handleAddSub(cat.id)}>
                        {formatMessage(messages.add)}
                      </button>
                    </li>
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

CategoryTree.propTypes = {
  categoryDetails: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
};

export default injectIntl(withStyles(bt, s)(CategoryTree));
